import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * ThemeProvider — manages light/dark/system theme.
 *
 *  - mode    : 'system' | 'light' | 'dark'  (user's stored preference)
 *  - resolved: 'light'  | 'dark'             (what's actually applied)
 *
 * Applies `html.dark` and `data-theme="dark|light"` so:
 *   - Tailwind `dark:` variants work (config: darkMode: 'class')
 *   - Custom CSS overrides under `html.dark .bg-[#FFFFFF]` etc. work
 *   - shadcn HSL vars under `.dark` selector work
 *
 * On every change, briefly applies `html.theme-transitioning` so global
 * CSS adds a soft 320ms cross-fade across bg/border/text colors.
 */

const STORAGE_KEY = 'asap_theme';
const ThemeContext = createContext(null);

function readSystem() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredMode() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {}
  return 'system';
}

function applyTheme(resolved, animate = true) {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (animate) {
    html.classList.add('theme-transitioning');
    window.setTimeout(() => html.classList.remove('theme-transitioning'), 360);
  }
  html.classList.toggle('dark', resolved === 'dark');
  html.setAttribute('data-theme', resolved);
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => readStoredMode());
  const [systemPref, setSystemPref] = useState(() => readSystem());

  const resolved = mode === 'system' ? systemPref : mode;

  // Apply theme on resolved change
  useEffect(() => {
    applyTheme(resolved, true);
  }, [resolved]);

  // Initial apply (no animation, prevents first-paint flash transition)
  useEffect(() => {
    applyTheme(resolved, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to system theme changes when in 'system' mode
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPref(e.matches ? 'dark' : 'light');
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  const setMode = useCallback((next) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
